/**
 * PHI MASK — the axios net.
 *
 * WHY AN INTERCEPTOR AND NOT ~100 CALL SITES
 *   Audited 2026-08-26: this repo has ZERO `axios.create()` calls. All 88 files
 *   that import axios — including all 40 Vuex store modules — share the same
 *   default singleton. So ONE pair of interceptors registered here covers
 *   essentially every read and write path in the app, across all 547 components,
 *   including exports and print views (which read the same store data).
 *
 *   The previous demo mode failed precisely because it was opt-in per call site.
 *   Masking at the boundary means a component must actively bypass the net to
 *   leak, and every FUTURE feature is covered without anyone remembering.
 *
 * WHAT IS *NOT* COVERED (handled separately, Phase 2)
 *   - Firestore reads (conversations, tasks, kanban, notifications)
 *   - Algolia search hits (task titles + comments)
 *   - Twilio Conversations attributes
 *   The 16 raw fetch() calls in src/ were audited and return no patient data
 *   (bot API, S3 file downloads, version.json, wiki/form uploads).
 *
 * COST WHEN OFF
 *   One boolean check per response. `isMaskOn()` short-circuits before any
 *   traversal, so a normal production session pays essentially nothing.
 */

import axios from 'axios';
import { isMaskOn } from '../utils/phiMaskState';
import { maskDeep } from '../utils/phiMask';

/* ---------------------------------------------------------------------------
 * SEND BLOCKING
 *
 * With masking on, a read has been replaced with a fake — so any outbound
 * message built from that read is now wrong. Concretely:
 * store/conversationsV2.js:1483 builds the SMS body from `patientData.FName`,
 * so an unblocked send would text a real patient "Hello Maria".
 *
 * Blocking at the REQUEST interceptor rather than at the ~8 send call sites is
 * the same argument as the response side: one place, cannot be forgotten by a
 * future feature. It also removes the on-camera hazard of accidentally texting
 * a real patient mid-recording.
 *
 * ALLOW takes precedence over BLOCK. Test-sends target a number/email the staff
 * member typed themselves, never a patient, and blocking them would make the
 * test-send feature impossible to demonstrate — which is the point of the video.
 * ------------------------------------------------------------------------- */
const SEND_ALLOW = [
   /\/send-test(-sms)?(\?|$)/i,        // campaigns: staff sends to their own device
   /\/email-scheduler\/test(\?|$)/i,
   /\/email-settings\//i,              // sender/domain verification, not a send
   /\/slack\/send(\?|$)/i,             // internal team notification
];

const SEND_BLOCK = [
   /\/send-text-msg(\?|$)/i,
   /\/send-email-msg(\?|$)/i,
   /\/send-message(\?|$)/i,
   /\/send-review-request(\?|$)/i,
   /\/send-tx-link(\?|$)/i,
   /\/post-tc-message(\?|$)/i,
   /\/sendAction(\?|$)/i,
   /\/campaigns\/[^/]+\/send(\?|$)/i,  // NOT /send-test — anchored, see SEND_ALLOW
   /\/trigger-autorecall-call(\?|$)/i,
   /\/autorecall-batch(\?|$)/i,
];

export function isBlockedSend(url) {
   if (!url) return false;
   const u = String(url);
   for (let i = 0; i < SEND_ALLOW.length; i++) {
      if (SEND_ALLOW[i].test(u)) return false;
   }
   for (let i = 0; i < SEND_BLOCK.length; i++) {
      if (SEND_BLOCK[i].test(u)) return true;
   }
   return false;
}

/** Marker so callers can tell a demo-block apart from a network failure. */
export const PHI_MASK_BLOCKED = 'PHI_MASK_BLOCKED';

let notify = null;
/** main.js hands us the toast fn; kept injectable so tests need no Vue. */
export function setNotifier(fn) {
   notify = fn;
}

export function installPhiMaskInterceptor() {
   axios.interceptors.request.use((config) => {
      if (!isMaskOn()) return config;
      if (!isBlockedSend(config && config.url)) return config;

      if (notify) {
         notify('Demo mask is ON — message not sent. Turn it off in the header to send for real.');
      }
      const err = new Error('Blocked by demo mask: outbound sends are disabled while patient data is masked.');
      err.code = PHI_MASK_BLOCKED;
      err.config = config;
      return Promise.reject(err);
   });

   axios.interceptors.response.use(
      (response) => {
         if (!isMaskOn()) return response;
         try {
            if (response && response.data && typeof response.data === 'object') {
               maskDeep(response.data);
            }
         } catch (e) {
            // Masking must never blank the page mid-recording, so the response
            // is let through — but a partial mask is a LEAK, and a silent leak
            // is the whole failure mode this rewrite exists to kill. Fail LOUDLY
            // so the recording gets stopped rather than published.
            console.error('[phiMask] failed to mask a response; data may be UNMASKED:', e);
            if (notify) {
               notify('Demo mask FAILED on this request — real patient data may be visible. Stop recording.');
            }
         }
         return response;
      },
      (error) => Promise.reject(error)
   );
}
